import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, FloppyDisk, ArrowsClockwise, FilePdf, FileZip, Warning, CheckCircle, Clock,
  PaperPlaneTilt, Trash, AirplaneTilt, Info,
} from '@phosphor-icons/react';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AddButton, CodedSelect, FieldGrid, InjuryTable } from '../components/adrep/AdrepFields';

const API = '/api/eccairs';
const STATUS_STYLE = {
  sent: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-red-100 text-red-800',
  due_soon: 'bg-amber-100 text-amber-800',
  due_now: 'bg-amber-100 text-amber-800',
  open: 'bg-slate-100 text-slate-700',
  unknown: 'bg-slate-100 text-slate-500',
};
const STATUS_LABEL = { sent: 'Sent', overdue: 'Overdue', due_soon: 'Due soon', due_now: 'Due now', open: 'Open', unknown: 'Unknown' };
const CHANNELS = [
  { key: 'e2_online_form', label: 'ECCAIRS 2 online form' },
  { key: 'e5x_file', label: 'E5X file transfer' },
  { key: 'e2_api', label: 'ECCAIRS 2 API' },
  { key: 'email', label: 'Email' },
  { key: 'other', label: 'Other' },
];

function icaoLabel(value) {
  if (value === true) return { text: 'Required to ICAO', style: 'text-blue-800 bg-blue-50 border-blue-200' };
  if (value === false) return { text: 'Not required to ICAO', style: 'text-slate-600 bg-slate-50 border-slate-200' };
  return { text: 'ICAO requirement not yet known', style: 'text-amber-800 bg-amber-50 border-amber-200' };
}

async function download(url, fallbackName) {
  const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename="([^"]+)"/);
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = match ? match[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
  return res;
}

export default function AdrepReporting() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState(null);
  const [view, setView] = useState(null);
  const [record, setRecord] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [e5x, setE5x] = useState(null);
  const [submission, setSubmission] = useState({ report_type: 'notification', channel: 'e2_online_form', submitted_at: '', reference: '', recipients: '', notes: '' });

  const applyView = (data) => {
    setView(data);
    setRecord(data.record);
    setDirty(false);
  };

  const load = useCallback(async () => {
    setError('');
    try {
      const [cat, rec] = await Promise.all([
        axios.get(`${API}/catalogue`, { withCredentials: true }),
        axios.get(`${API}/investigations/${id}`, { withCredentials: true }),
      ]);
      setCatalogue(cat.data);
      applyView(rec.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const update = (patch) => { setRecord((r) => ({ ...r, ...patch })); setDirty(true); setNotice(''); };

  const save = async () => {
    setSaving(true); setError(''); setNotice('');
    try {
      const { data } = await axios.put(`${API}/investigations/${id}`, record, { withCredentials: true });
      applyView(data);
      setNotice('Saved');
      setE5x(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const refill = async () => {
    if (dirty && !window.confirm('Save your changes first? Unsaved edits will be lost.')) return;
    try {
      const { data } = await axios.post(`${API}/investigations/${id}/prefill`, {}, { withCredentials: true });
      applyView(data);
      setNotice('Empty fields filled from the investigation');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const getPdf = async (report) => {
    try { await download(`${API}/investigations/${id}/pdf/${report}`, `ADREP_${report}.pdf`); }
    catch (err) { setError('Could not generate the PDF'); }
  };

  const loadE5x = async () => {
    try {
      const { data } = await axios.get(`${API}/investigations/${id}/e5x/preview`, { withCredentials: true });
      setE5x(data);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const getE5x = async () => {
    try { await download(`${API}/investigations/${id}/e5x`, 'occurrence.e5x'); }
    catch (err) { setError('Could not generate the E5X file'); }
  };

  const recordSubmission = async () => {
    try {
      const { data } = await axios.post(`${API}/investigations/${id}/submissions`, submission, { withCredentials: true });
      applyView(data);
      setSubmission((s) => ({ ...s, reference: '', recipients: '', notes: '' }));
      setNotice('Submission recorded');
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submitE2 = async (report) => {
    if (!window.confirm('Send this report to ECCAIRS 2 now?')) return;
    try {
      const { data } = await axios.post(`${API}/investigations/${id}/submit-e2`, { report_type: report }, { withCredentials: true });
      applyView(data);
      setNotice('Sent to ECCAIRS 2');
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const missingBySection = useMemo(() => {
    const sets = { occurrence: new Set(), narrative: new Set(), aircraft: {} };
    Object.values(view?.completeness || {}).forEach((c) => c.missing.forEach((m) => {
      if (m.section === 'aircraft' && m.index != null) {
        (sets.aircraft[m.index] = sets.aircraft[m.index] || new Set()).add(m.key);
      } else if (sets[m.section] instanceof Set) sets[m.section].add(m.key);
    }));
    return sets;
  }, [view]);

  if (error && !view) {
    return (
      <div className="max-w-3xl mx-auto mt-10 bg-red-50 border border-red-200 text-red-800 rounded-xl p-6" data-testid="adrep-error">
        {error}
        <div className="mt-4"><Button variant="outline" onClick={() => navigate(`/investigations/${id}`)}>Back to investigation</Button></div>
      </div>
    );
  }
  if (!view || !catalogue || !record) return <div className="flex items-center justify-center h-full text-slate-500">Loading ECCAIRS reporting...</div>;

  const { fields, value_lists: lists, taxonomy } = catalogue;
  const disabled = !view.can_edit;
  const reportKeys = ['notification', 'preliminary', 'data'];
  const deadlineByKey = Object.fromEntries(view.deadlines.map((d) => [d.key, d]));
  const interim = deadlineByKey.interim_statement;

  const setAircraft = (index, value) => update({ aircraft: record.aircraft.map((a, i) => (i === index ? value : a)) });

  return (
    <div className="max-w-6xl mx-auto pb-24" data-testid="adrep-page">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <Button variant="outline" size="sm" className="rounded-lg mt-1" onClick={() => navigate(`/investigations/${id}`)} aria-label="Back to investigation">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>ECCAIRS / ADREP Reporting</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1">
            <span className="font-mono">{view.investigation.number}</span>
            <span className="inline-flex items-center gap-1"><AirplaneTilt size={12} />ICAO Annex 13, Chapter 7</span>
            {record.updated_at && <span>Last saved {new Date(record.updated_at).toLocaleString()} by {record.updated_by}</span>}
          </div>
        </div>
        {view.can_edit && (
          <Button variant="outline" className="rounded-lg" onClick={refill} data-testid="adrep-prefill">
            <ArrowsClockwise size={16} className="mr-2" />Fill from investigation
          </Button>
        )}
      </div>

      {!taxonomy.official && (
        <div className="flex gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 mb-6 text-sm" data-testid="taxonomy-notice">
          <Info size={20} className="shrink-0 mt-0.5" />
          <div>
            <b>Using the built-in ADREP starter taxonomy.</b> Occurrence class, categories, event types and phases carry
            official ECCAIRS codes. {taxonomy.pending_lists.length} other lists are coded once your administrator installs the
            official ECCAIRS taxonomy pack. PDF reports are complete either way.
          </div>
        </div>
      )}

      {/* Report status cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {reportKeys.map((key) => {
          const c = view.completeness[key];
          const d = deadlineByKey[key];
          const icao = icaoLabel(view.applicability[key]);
          return (
            <div key={key} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`report-card-${key}`}>
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-semibold text-slate-900 text-sm">{c.label}</h2>
                {d && <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLE[d.status]}`}>{STATUS_LABEL[d.status]}</span>}
              </div>
              <div className={`inline-block mt-2 px-2 py-0.5 rounded border text-xs ${icao.style}`}>{icao.text}</div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{c.percent}%</div>
                  <div className="text-xs text-slate-500">{c.complete} of {c.required} required items</div>
                </div>
                <div className="text-right text-xs text-slate-500">
                  <Clock size={14} className="inline mr-1" />
                  {d?.due ? new Date(d.due).toLocaleDateString() : d?.rule}
                </div>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full mt-3 overflow-hidden">
                <div className={`h-full ${c.percent === 100 ? 'bg-emerald-500' : 'bg-blue-600'}`} style={{ width: `${c.percent}%` }} />
              </div>
              {c.missing.length > 0 && (
                <details className="mt-3 text-xs text-slate-600">
                  <summary className="cursor-pointer text-red-700">{c.missing.length} missing</summary>
                  <ul className="mt-2 space-y-0.5 list-disc pl-4 max-h-40 overflow-y-auto">
                    {c.missing.map((m, i) => <li key={i}>{m.label}</li>)}
                  </ul>
                </details>
              )}
              <Button variant="outline" size="sm" className="w-full mt-4 rounded-lg" onClick={() => getPdf(key)} data-testid={`pdf-${key}`}>
                <FilePdf size={16} className="mr-2" />Download PDF
              </Button>
            </div>
          );
        })}
      </div>
      {interim && (
        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 mb-6">
          Final report not yet released: next interim statement due {new Date(interim.due).toLocaleDateString()} (on each anniversary of the occurrence).
        </div>
      )}

      {(error || notice) && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm border ${error ? 'bg-red-50 border-red-200 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
          {error || notice}
        </div>
      )}

      <Tabs defaultValue="occurrence">
        <TabsList className="bg-slate-100 rounded-xl p-1 mb-6 flex-wrap h-auto">
          <TabsTrigger value="occurrence" className="rounded-lg">Occurrence</TabsTrigger>
          <TabsTrigger value="aircraft" className="rounded-lg">Aircraft &amp; injuries</TabsTrigger>
          <TabsTrigger value="events" className="rounded-lg">Events &amp; factors</TabsTrigger>
          <TabsTrigger value="narrative" className="rounded-lg">Narrative</TabsTrigger>
          <TabsTrigger value="submit" className="rounded-lg" onClick={() => !e5x && loadE5x()}>Export &amp; submissions</TabsTrigger>
        </TabsList>

        <TabsContent value="occurrence">
          <section className="bg-white rounded-xl border border-slate-200 p-6">
            <FieldGrid fields={fields.occurrence} values={record.occurrence} valueLists={lists} disabled={disabled}
              missingKeys={missingBySection.occurrence} onChange={(v) => update({ occurrence: v })} />
          </section>
        </TabsContent>

        <TabsContent value="aircraft">
          <div className="space-y-6">
            {record.aircraft.map((ac, index) => (
              <section key={index} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`aircraft-${index}`}>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-semibold text-slate-900">Aircraft {index + 1}{ac.registration ? `: ${ac.registration}` : ''}</h2>
                  {!disabled && record.aircraft.length > 1 && (
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => update({
                      aircraft: record.aircraft.filter((_, i) => i !== index),
                      events: record.events.map((e) => ({ ...e, aircraft_index: e.aircraft_index === index ? 0 : (e.aircraft_index > index ? e.aircraft_index - 1 : e.aircraft_index) })),
                    })}><Trash size={16} className="mr-1" />Remove</Button>
                  )}
                </div>
                <FieldGrid fields={fields.aircraft} values={ac} valueLists={lists} disabled={disabled}
                  missingKeys={missingBySection.aircraft[index]} onChange={(v) => setAircraft(index, { ...v, injuries: ac.injuries })} />
                <h3 className="font-medium text-slate-900 mt-8 mb-3">Persons on board and injuries <span className="text-red-500">*</span></h3>
                <InjuryTable rows={fields.injury_rows} columns={fields.injury_columns} value={ac.injuries || {}} disabled={disabled}
                  onChange={(injuries) => setAircraft(index, { ...ac, injuries })} />
              </section>
            ))}
            {!disabled && record.aircraft.length < 10 && (
              <AddButton onClick={() => update({ aircraft: [...record.aircraft, { injuries: {} }] })} testId="add-aircraft">Add aircraft (e.g. for a mid-air collision)</AddButton>
            )}
          </div>
        </TabsContent>

        <TabsContent value="events">
          <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h2 className="font-semibold text-slate-900 mb-1">Events</h2>
            <p className="text-sm text-slate-500 mb-4">Code each event in the order it happened; consequential events follow the events that caused them.</p>
            <div className="space-y-3">
              {record.events.map((ev, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-[2fr_1.4fr_1fr_auto] gap-3 items-end border border-slate-100 rounded-lg p-3" data-testid={`event-${index}`}>
                  <div><Label className="text-xs">Event type #390</Label>
                    <CodedSelect entries={lists.event_type} value={ev.event_type} disabled={disabled}
                      onChange={(v) => update({ events: record.events.map((e, i) => (i === index ? { ...e, event_type: v } : e)) })} /></div>
                  <div><Label className="text-xs">Phase #391</Label>
                    <CodedSelect entries={lists.phase} value={ev.event_phase} disabled={disabled}
                      onChange={(v) => update({ events: record.events.map((e, i) => (i === index ? { ...e, event_phase: v } : e)) })} /></div>
                  <div><Label className="text-xs">Aircraft</Label>
                    <Select value={String(ev.aircraft_index ?? 0)} disabled={disabled}
                      onValueChange={(v) => update({ events: record.events.map((e, i) => (i === index ? { ...e, aircraft_index: Number(v) } : e)) })}>
                      <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>{record.aircraft.map((a, i) => <SelectItem key={i} value={String(i)}>{a.registration || `Aircraft ${i + 1}`}</SelectItem>)}</SelectContent>
                    </Select></div>
                  {!disabled && <Button variant="ghost" size="sm" className="text-red-600" aria-label="Remove event"
                    onClick={() => update({ events: record.events.filter((_, i) => i !== index) })}><Trash size={16} /></Button>}
                </div>
              ))}
              {record.events.length === 0 && <p className="text-sm text-slate-400">No events coded yet.</p>}
              {!disabled && <AddButton onClick={() => update({ events: [...record.events, { aircraft_index: 0 }] })} testId="add-event">Add event</AddButton>}
            </div>
          </section>

          <section className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-1">Descriptive and explanatory factors</h2>
            <p className="text-sm text-slate-500 mb-4">Descriptive factors say what happened; explanatory factors say why (SHELL model).</p>
            <div className="space-y-3">
              {record.factors.map((f, index) => {
                const set = (k, v) => update({ factors: record.factors.map((x, i) => (i === index ? { ...x, [k]: v } : x)) });
                return (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr_1fr_1fr_auto] gap-3 items-end border border-slate-100 rounded-lg p-3">
                    <div><Label className="text-xs">Type</Label>
                      <Select value={f.kind || 'descriptive'} onValueChange={(v) => set('kind', v)} disabled={disabled}>
                        <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="descriptive">Descriptive</SelectItem><SelectItem value="explanatory">Explanatory</SelectItem></SelectContent>
                      </Select></div>
                    <div><Label className="text-xs">Subject</Label><Input value={f.subject || ''} onChange={(e) => set('subject', e.target.value)} disabled={disabled} className="rounded-lg" /></div>
                    <div><Label className="text-xs">Modifier</Label><Input value={f.modifier || ''} onChange={(e) => set('modifier', e.target.value)} disabled={disabled} className="rounded-lg" /></div>
                    <div><Label className="text-xs">Person / organisation</Label><Input value={f.person_org || ''} onChange={(e) => set('person_org', e.target.value)} disabled={disabled} className="rounded-lg" /></div>
                    {!disabled && <Button variant="ghost" size="sm" className="text-red-600" aria-label="Remove factor"
                      onClick={() => update({ factors: record.factors.filter((_, i) => i !== index) })}><Trash size={16} /></Button>}
                  </div>
                );
              })}
              {!disabled && <AddButton onClick={() => update({ factors: [...record.factors, { kind: 'descriptive' }] })} testId="add-factor">Add factor</AddButton>}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="narrative">
          <section className="bg-white rounded-xl border border-slate-200 p-6">
            <FieldGrid fields={fields.narrative} values={record.narrative} valueLists={lists} disabled={disabled}
              missingKeys={missingBySection.narrative} onChange={(v) => update({ narrative: v })} />
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {[['Probable causes', view.analysis.probable_causes], ['Contributing factors', view.analysis.contributing_factors]].map(([title, items]) => (
                <div key={title} className="bg-slate-50 rounded-lg p-4">
                  <div className="font-medium text-slate-900 mb-2">{title}</div>
                  {items.length ? <ol className="list-decimal pl-4 space-y-1 text-slate-700">{items.map((t, i) => <li key={i}>{t}</li>)}</ol>
                    : <p className="text-slate-400">Not yet in the synthesis</p>}
                </div>
              ))}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="font-medium text-slate-900 mb-2">Safety recommendations</div>
                {view.analysis.safety_recommendations.length
                  ? <ul className="space-y-1 text-slate-700">{view.analysis.safety_recommendations.map((r, i) => <li key={i}><span className="font-mono text-xs">{r.number}</span> {r.addressee}</li>)}</ul>
                  : <p className="text-slate-400">None issued</p>}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">These come from the synthesis and safety recommendations modules and are included in the ADREP data report.</p>
          </section>
        </TabsContent>

        <TabsContent value="submit">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-900 mb-1">E5X file</h2>
              <p className="text-sm text-slate-500 mb-4">ECCAIRS exchange file for import into an ECCAIRS 2 environment.</p>
              {e5x ? (
                <div className={`rounded-lg border p-4 text-sm mb-4 ${e5x.draft ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`} data-testid="e5x-status">
                  <div className="flex items-center gap-2 font-medium mb-1">
                    {e5x.draft ? <Warning size={16} /> : <CheckCircle size={16} />}{e5x.draft ? 'Draft file' : 'Ready for validation'}
                  </div>
                  <p>{e5x.exported_attributes} attributes exported. {e5x.advice}</p>
                  {e5x.skipped.length > 0 && <p className="mt-2">Not coded yet: {e5x.skipped.map((s) => s.label).join(', ')}.</p>}
                </div>
              ) : <Button variant="outline" size="sm" className="mb-4 rounded-lg" onClick={loadE5x}>Check E5X status</Button>}
              <Button onClick={getE5x} className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white" disabled={dirty} data-testid="download-e5x">
                <FileZip size={16} className="mr-2" />Download E5X
              </Button>
              {dirty && <p className="text-xs text-amber-700 mt-2">Save your changes before exporting.</p>}
              {view.e2_api_enabled && view.can_edit && (
                <div className="mt-6 border-t border-slate-100 pt-4">
                  <h3 className="font-medium text-slate-900 mb-2">Send through the ECCAIRS 2 API</h3>
                  <div className="flex flex-wrap gap-2">
                    {reportKeys.map((k) => (
                      <Button key={k} variant="outline" size="sm" className="rounded-lg" disabled={dirty} onClick={() => submitE2(k)}>
                        <PaperPlaneTilt size={14} className="mr-1" />{view.completeness[k].label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-900 mb-1">Record a submission</h2>
              <p className="text-sm text-slate-500 mb-4">Log each report sent to ICAO or other States, so deadlines show as met.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Report</Label>
                  <Select value={submission.report_type} onValueChange={(v) => setSubmission({ ...submission, report_type: v })} disabled={disabled}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{reportKeys.map((k) => <SelectItem key={k} value={k}>{view.completeness[k].label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Sent via</Label>
                  <Select value={submission.channel} onValueChange={(v) => setSubmission({ ...submission, channel: v })} disabled={disabled}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{CHANNELS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Date sent</Label><Input type="date" value={submission.submitted_at} onChange={(e) => setSubmission({ ...submission, submitted_at: e.target.value })} disabled={disabled} className="rounded-lg" /></div>
                <div><Label className="text-xs">Reference</Label><Input value={submission.reference} onChange={(e) => setSubmission({ ...submission, reference: e.target.value })} placeholder="E2 occurrence number" disabled={disabled} className="rounded-lg" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Sent to</Label><Input value={submission.recipients} onChange={(e) => setSubmission({ ...submission, recipients: e.target.value })} placeholder="ICAO; State of Registry; State of the Operator..." disabled={disabled} className="rounded-lg" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={2} value={submission.notes} onChange={(e) => setSubmission({ ...submission, notes: e.target.value })} disabled={disabled} className="rounded-lg" /></div>
              </div>
              {!disabled && <Button className="mt-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" onClick={recordSubmission} data-testid="record-submission">Record submission</Button>}
            </section>
          </div>

          <section className="bg-white rounded-xl border border-slate-200 p-6 mt-6">
            <h2 className="font-semibold text-slate-900 mb-4">Submission history</h2>
            {record.submissions?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="submission-history">
                  <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-4 font-medium">Date</th><th className="py-2 pr-4 font-medium">Report</th><th className="py-2 pr-4 font-medium">Via</th>
                    <th className="py-2 pr-4 font-medium">Reference</th><th className="py-2 pr-4 font-medium">Sent to</th><th className="py-2 font-medium">Recorded by</th>
                  </tr></thead>
                  <tbody>
                    {[...record.submissions].reverse().map((s, i) => (
                      <tr key={i} className="border-b border-slate-100 align-top">
                        <td className="py-2 pr-4 whitespace-nowrap">{(s.submitted_at || '').slice(0, 10)}</td>
                        <td className="py-2 pr-4">{s.report_label}</td>
                        <td className="py-2 pr-4">{CHANNELS.find((c) => c.key === s.channel)?.label || s.channel}</td>
                        <td className="py-2 pr-4 font-mono text-xs">{s.reference}</td>
                        <td className="py-2 pr-4">{s.recipients}</td>
                        <td className="py-2 text-slate-500">{s.recorded_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-slate-400">Nothing sent yet.</p>}
          </section>
        </TabsContent>
      </Tabs>

      {view.can_edit && (
        <div className="fixed bottom-0 right-0 left-0 lg:left-64 bg-white/95 backdrop-blur border-t border-slate-200 px-6 py-3 flex items-center justify-end gap-3 z-20">
          {dirty && <span className="text-sm text-amber-700">Unsaved changes</span>}
          <Button onClick={save} disabled={saving || !dirty} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="adrep-save">
            <FloppyDisk size={16} className="mr-2" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}
